import * as React from "react";
import { useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import TopBar from "./TopBar";
import { Button, Card, MenuItem, Select, TextField } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { newConversation, sendMessage } from "./mutations";
import { listConversations } from "./queries";
import Add from "@mui/icons-material/Add";
import RequestTimer from "./RequestTimer";

function Copyright() {
  return (
    <Typography
      variant="body2"
      align="center"
      sx={{
        color: "text.secondary",
      }}
    >
      {"Copyright © "}
      <Link color="inherit" href="https://arsenicfarm.com/">
        Arsenic Farm
      </Link>{" "}
      {new Date().getFullYear()}.
    </Typography>
  );
}

export default function App() {
  const [message, setMessage] = React.useState<string>("");
  const [conversationId, setConversationId] = React.useState<string>("");
  const [apiResponse, setApiResponse] = React.useState<string>("");
  const [requestTimes, setRequestTimes] = useState<number[]>([]);
  const queryClient = useQueryClient();

  const listConversationsQuery = useQuery({
    queryKey: ["listConversations"],
    queryFn: listConversations,
  });

  const sendMessageMutation = useMutation({
    mutationFn: sendMessage,
    onSuccess: (data: any) => {
      setApiResponse(JSON.stringify(data.response, null, 2));
    },
    onError: (error: Error) => {
      console.error("There was a problem with the fetch operation:", error);
    },
  });

  const newConversationMutation = useMutation({
    mutationFn: newConversation,
    onSuccess: (data: any) => {
      setApiResponse(JSON.stringify(data.response, null, 2));
      queryClient.invalidateQueries({ queryKey: ["listConversations"] });
    },
    onError: (error: Error) => {
      console.error("There was a problem with the fetch operation:", error);
    },
  });

  const handleSend = () => {
    setApiResponse("");
    listConversationsQuery.data?.conversations[0] &&
      sendMessageMutation.mutate({
        conversationId: conversationId,
        message: message,
      });
  };

  const handleNewConversation = () => {
    newConversationMutation.mutate({
      llmModelId: "anthropic.claude-3-5-sonnet-20241022-v2:0",
      username: "aaroncbrooks",
    });
  };

  return (
    <React.Fragment>
      <TopBar />
      <Container maxWidth="sm">
        <Box sx={{ my: 4 }}>
          <Typography variant={"body1"}>Talk to Pwyll!</Typography>
          <Typography variant={"body2"}>
            conversation id:{" "}
            {listConversationsQuery.isPending
              ? "loading..."
              : (conversationId ?? "unknown")}
          </Typography>
          <Select
            labelId="conversation-id"
            value={conversationId}
            label="Conversation ID"
            variant={"outlined"}
            onChange={(event) => setConversationId(event.target.value)}
          >
            {listConversationsQuery.data?.conversations.map(
              (convId: string) => {
                return <MenuItem value={convId}>{convId}</MenuItem>;
              },
            )}
          </Select>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNewConversation}
            startIcon={<Add />}
          />
          <TextField
            label="Message"
            multiline
            variant="outlined"
            fullWidth
            value={message}
            onChange={(e) => {
              setApiResponse("");
              setMessage(e.target.value);
            }}
            sx={{ my: 2 }}
          />
          <Button variant="contained" color="primary" onClick={handleSend}>
            {sendMessageMutation.isPending ? "Sending..." : "Send"}
          </Button>
          <RequestTimer
            isLoading={sendMessageMutation.isPending}
            onComplete={(time) => setRequestTimes((prev) => [...prev, time])}
          />
          {requestTimes.length > 0 && !sendMessageMutation.isPending && (
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Last {Math.min(3, requestTimes.length)} request times:
              {requestTimes
                .slice(-3)
                .map(
                  (time, i) =>
                    ` ${Math.floor(time / 60)}:${(time % 60).toString().padStart(2, "0")}${i < Math.min(3, requestTimes.length) - 1 ? "," : ""}`,
                )}
            </Typography>
          )}
          {apiResponse ? (
            <Typography variant="body1" sx={{ mt: 2 }}>
              Pwyll says:
              <Card>
                {apiResponse.split("\\n").map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </Card>
            </Typography>
          ) : (
            <Typography variant="body1" sx={{ mt: 2 }}></Typography>
          )}
          <Box sx={{ my: 2 }} />
          <Copyright />
        </Box>
      </Container>
    </React.Fragment>
  );
}
